import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Copy, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, publicShopUrl, SHOP_CATEGORIES } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { ImageUploader } from "../../components/ImageUploader";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const KEYS = ["name", "category", "description", "open_time", "close_time", "is_open", "whatsapp", "breaking_news", "profile_photo", "header_photo"];
const pick = (s) => Object.fromEntries(KEYS.map((k) => [k, s[k] ?? ""]));

const Section = ({ title, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4 fade-up">
    <h3 className="font-bold">{title}</h3>{children}
  </section>
);
const F = ({ label, children, hint }) => (
  <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
);

export default function ShopSettings() {
  const { shop, setShop } = useOutletContext();
  const [form, setForm] = useState(pick(shop));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const url = publicShopUrl(shop.slug);

  const save = async (next = form, msg = "Pengaturan toko disimpan") => {
    setBusy(true);
    try {
      const { data } = await api.put("/shop", { ...next, is_open: !!next.is_open });
      setShop(data.shop);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = (v) => {
    setForm((f) => ({ ...f, is_open: v }));
    save({ ...pick(shop), is_open: v }, v ? "Toko dibuka" : "Toko ditutup");
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} data-testid="shop-settings-form">
      <PageHeader title="Toko" subtitle="Atur tampilan dan informasi toko publik kamu.">
        <Button type="button" variant="outline" asChild className="rounded-xl"><a href={url} target="_blank" rel="noreferrer" data-testid="shop-preview-link"><ExternalLink className="h-4 w-4 mr-1" />Lihat toko</a></Button>
        <Button type="submit" disabled={busy} className="rounded-xl" data-testid="shop-save-button">{busy ? "Menyimpan..." : "Simpan perubahan"}</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Foto toko">
            <F label="Foto header toko"><ImageUploader multiple={false} aspect="aspect-[3/1]" value={form.header_photo} onChange={(v) => setForm((f) => ({ ...f, header_photo: v }))} testId="shop-header-photo" /></F>
            <div className="w-32"><F label="Foto profil"><ImageUploader multiple={false} value={form.profile_photo} onChange={(v) => setForm((f) => ({ ...f, profile_photo: v }))} testId="shop-profile-photo" /></F></div>
          </Section>
          <Section title="Informasi toko">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Nama toko"><Input required value={form.name} onChange={set("name")} data-testid="shop-name-input" /></F>
              <F label="Kategori toko">
                <Select value={form.category || undefined} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="shop-category-select"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>{SHOP_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`shop-category-${c}`}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <F label="Deskripsi toko"><Textarea rows={4} value={form.description} onChange={set("description")} data-testid="shop-description-input" /></F>
            <F label="Nomor WhatsApp toko" hint="Format: 08xxx atau 628xxx"><Input value={form.whatsapp} onChange={set("whatsapp")} data-testid="shop-whatsapp-input" /></F>
            <F label="Breaking news" hint="Teks berjalan (marquee) di bagian atas toko publik. Kosongkan untuk menyembunyikan.">
              <Textarea rows={2} value={form.breaking_news} onChange={set("breaking_news")} data-testid="shop-breaking-news-input" />
            </F>
          </Section>
        </div>
        <div className="space-y-6">
          <Section title="Status toko">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4">
              <div>
                <p className="font-semibold" data-testid="shop-open-status">{form.is_open ? "Toko buka" : "Toko tutup"}</p>
                <p className="text-xs text-slate-500">Jika tutup, halaman publik menampilkan "Toko Tutup".</p>
              </div>
              <Switch checked={!!form.is_open} onCheckedChange={toggleOpen} data-testid="shop-open-toggle" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Jam buka"><Input type="time" value={form.open_time} onChange={set("open_time")} data-testid="shop-open-time-input" /></F>
              <F label="Jam tutup"><Input type="time" value={form.close_time} onChange={set("close_time")} data-testid="shop-close-time-input" /></F>
            </div>
          </Section>
          <Section title="Link toko publik">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <Lock className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="truncate font-mono text-sm" data-testid="shop-slug-display">{window.location.host}/{shop.slug}</span>
              <button type="button" className="ml-auto rounded p-1 hover:bg-white" data-testid="shop-copy-link-button"
                onClick={() => { navigator.clipboard.writeText(url); toast.success("Link disalin"); }}><Copy className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-slate-500">Link toko dikunci dan tidak bisa diubah atau dihapus.</p>
          </Section>
        </div>
      </div>
    </form>
  );
}
